import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  LegacyCard,
  IndexTable,
  Text,
  Badge,
  Spinner,
  BlockStack,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useNavigate } from "react-router-dom";
import { formatDate, formatPlate } from "../../utility/funcs";

function Checklists() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("car_checklists")
        .select(
          "id, notes, created_at, car:cars(plate, model:car_models(name, show_as)), staff:users!staff_id(name)"
        )
        .order("created_at", { ascending: false });

      if (!data) {
        setLoading(false);
        return;
      }

      // Fetch counts per checklist in parallel
      const ids = data.map((c) => c.id);
      const { data: counts } = await supabase
        .from("car_checklist_items")
        .select("checklist_id, answer")
        .in("checklist_id", ids);

      const countMap = {};
      (counts || []).forEach((i) => {
        if (!countMap[i.checklist_id]) countMap[i.checklist_id] = { total: 0, fail: 0 };
        countMap[i.checklist_id].total++;
        if (i.answer === "negative") countMap[i.checklist_id].fail++;
      });

      setRows(
        data.map((c) => ({
          ...c,
          item_count: countMap[c.id]?.total || 0,
          fail_count: countMap[c.id]?.fail || 0,
        }))
      );
      setLoading(false);
    };
    fetchData();
  }, []);

  return (
    <AppWrapper>
      <Page title="Checklists">
        <BlockStack gap="400">
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <IndexTable
                resourceName={{ singular: "Checklist", plural: "Checklists" }}
                selectable={false}
                itemCount={rows.length}
                headings={[
                  { title: "Fecha" },
                  { title: "Auto" },
                  { title: "Staff" },
                  { title: "Items" },
                  { title: "Fallas" },
                  { title: "Notas" },
                ]}
              >
                {rows.map((r, index) => (
                  <IndexTable.Row
                    id={String(r.id)}
                    key={r.id}
                    position={index}
                    onClick={() => navigate(`/checklists/${r.id}`)}
                  >
                    <IndexTable.Cell>
                      <Text variant="bodySm">
                        {formatDate(r.created_at?.substring(0, 10))}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" fontWeight="semibold">
                        {formatPlate(r.car?.plate)}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        {r.car?.model?.show_as || r.car?.model?.name}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{r.staff?.name || "-"}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{r.item_count}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {r.fail_count > 0 ? (
                        <Badge tone="critical">{`${r.fail_count}`}</Badge>
                      ) : (
                        <Badge tone="success">0</Badge>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" tone="subdued">
                        {r.notes || "-"}
                      </Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Checklists;
